import React, { useCallback, useEffect, useState, useRef, lazy } from 'react';
const NAMES = ['María', 'Ana', 'Sofía', 'Valentina', 'Camila', 'Isabella', 'Lucía', 'Daniela', 'Mariana', 'Gabriela', 'Andrea', 'Fernanda', 'Alejandra', 'Carolina', 'Paola', 'Natalia', 'Diana', 'Laura', 'Claudia', 'Patricia', 'Mónica', 'Adriana', 'Verónica', 'Karla', 'Jessica', 'Lorena', 'Paulina', 'Renata', 'Ximena', 'Valeria'];
const CITIES = ['Miami', 'Houston', 'Los Angeles', 'Chicago', 'New York', 'Dallas', 'Phoenix', 'San Antonio', 'San Diego', 'Austin', 'Denver', 'Las Vegas', 'Orlando', 'Atlanta', 'San Francisco', 'Seattle', 'Portland', 'Sacramento', 'Tampa', 'Charlotte'];
interface Product {
  title: string;
  editionLabel: string;
  priceText: string;
  thumbnailUrl: string;
  weight?: number;
}
const FALLBACK_CATALOG: Product[] = [{
  title: 'Método Piel de Vidrio',
  editionLabel: 'Guía Maestra',
  priceText: '$9.00 USD',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/7AVKnA4fwHJWrePDV3uG4e/mockup-3.png',
  weight: 70
}, {
  title: 'Piel de Vidrio',
  editionLabel: 'Edición Digital',
  priceText: '$9.00 USD',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/7AVKnA4fwHJWrePDV3uG4e/mockup-3.png',
  weight: 30
}];
interface ToastData {
  name: string;
  city: string;
  product: Product;
}
type AnimationState = 'hidden' | 'entering' | 'visible' | 'exiting';
function getRandomInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}
function scanDOM(): Product[] {
  const scannedProducts: Product[] = [];
  const priceRegex = /\$\s?\d+(\.\d{2})?\s*(USD|usd)?/g;
  const allElements = document.body.querySelectorAll('*');
  const processedContainers = new Set<Element>();
  allElements.forEach(el => {
    if (el.children.length === 0 && el.textContent) {
      priceRegex.lastIndex = 0;
      if (!priceRegex.test(el.textContent)) return;
      let container: Element | null = el.parentElement;
      let depth = 0;
      while (container && depth < 5) {
        if (container.classList.contains('bg-white') || container.tagName === 'SECTION') {
          break;
        }
        container = container.parentElement;
        depth++;
      }
      if (container && !processedContainers.has(container)) {
        processedContainers.add(container);
        const img = container.querySelector('img');
        const heading = container.querySelector('h2, h3, h4');
        if (img && heading && img.src && heading.textContent) {
          priceRegex.lastIndex = 0;
          const priceMatch = el.textContent.match(priceRegex);
          if (priceMatch) {
            scannedProducts.push({
              title: heading.textContent.trim(),
              editionLabel: 'Edición Digital',
              priceText: priceMatch[0].trim(),
              thumbnailUrl: img.src
            });
          }
        }
      }
    }
  });
  return scannedProducts;
}
export function RecentPurchaseToast() {
  const [toast, setToast] = useState<ToastData | null>(null);
  const [animState, setAnimState] = useState<AnimationState>('hidden');
  const catalogRef = useRef<Product[]>(FALLBACK_CATALOG);
  const usedNamesRef = useRef<string[]>([]);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);
  const pickName = useCallback((): string => {
    const available = NAMES.filter(n => !usedNamesRef.current.includes(n));
    const pool = available.length > 0 ? available : NAMES;
    const name = pool[Math.floor(Math.random() * pool.length)];
    usedNamesRef.current.push(name);
    if (usedNamesRef.current.length > 10) {
      usedNamesRef.current.shift();
    }
    return name;
  }, []);
  const pickProduct = useCallback((): Product => {
    const catalog = catalogRef.current.length > 0 ? catalogRef.current : FALLBACK_CATALOG;
    const hasWeights = catalog.some(p => p.weight !== undefined);
    if (hasWeights) {
      const rand = Math.random() * 100;
      let sum = 0;
      for (const p of catalog) {
        sum += p.weight || 0;
        if (rand < sum) return p;
      }
    }
    return catalog[Math.floor(Math.random() * catalog.length)];
  }, []);
  const showToast = useCallback(() => {
    if (!isMountedRef.current) return;
    const newToast: ToastData = {
      name: pickName(),
      city: CITIES[Math.floor(Math.random() * CITIES.length)],
      product: pickProduct()
    };
    setToast(newToast);
    setAnimState('entering');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!isMountedRef.current) return;
        setAnimState('visible');
      });
    });
    timerRef.current = setTimeout(() => {
      if (!isMountedRef.current) return;
      setAnimState('exiting');
      timerRef.current = setTimeout(() => {
        if (!isMountedRef.current) return;
        setAnimState('hidden');
        setToast(null);
        const nextDelay = getRandomInt(8000, 15000);
        timerRef.current = setTimeout(() => {
          if (isMountedRef.current) showToast();
        }, nextDelay);
      }, 400);
    }, 4000);
  }, [pickName, pickProduct]);
  // Scan DOM after mount delay, then start toast cycle
  useEffect(() => {
    isMountedRef.current = true;
    const scanTimeout = setTimeout(() => {
      const scanned = scanDOM();
      if (scanned.length > 0) {
        catalogRef.current = scanned;
      }
    }, 1000);
    timerRef.current = setTimeout(() => {
      if (isMountedRef.current) showToast();
    }, 3000);
    return () => {
      isMountedRef.current = false;
      clearTimeout(scanTimeout);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [showToast]);
  if (!toast && animState === 'hidden') return null;
  const prefersReducedMotion = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let transitionClass = '';
  if (prefersReducedMotion) {
    transitionClass = animState === 'visible' ? 'opacity-100' : 'opacity-0 pointer-events-none';
  } else if (animState === 'hidden') {
    transitionClass = 'opacity-0 translate-y-full pointer-events-none';
  } else if (animState === 'entering') {
    transitionClass = 'opacity-0 translate-y-8 transition-all duration-500 ease-out';
  } else if (animState === 'visible') {
    transitionClass = 'opacity-100 translate-y-0 transition-all duration-500 ease-out';
  } else if (animState === 'exiting') {
    transitionClass = 'opacity-0 translate-y-8 transition-all duration-300 ease-in pointer-events-none';
  }
  return <div className={`fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] w-[calc(100vw-2rem)] max-w-sm bg-white rounded-xl shadow-2xl border border-cream-dark p-3 flex items-center gap-3 ${transitionClass}`} role="status" aria-live="polite" data-id="element-162">
      <div className="relative shrink-0" data-id="element-163">
        <img src={toast?.product?.thumbnailUrl} alt="" className="w-12 h-12 rounded-lg object-cover border border-brown-secondary/10" loading="lazy" decoding="async" data-id="element-164" />
        <div className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-green-500 rounded-full border-2 border-white" data-id="element-165" />
      </div>

      <div className="flex-1 min-w-0" data-id="element-166">
        <p className="text-sm text-brown-primary truncate" data-id="element-167">
          <strong className="font-bold" data-id="element-168">{toast?.name}</strong> en {toast?.city}{' '}
          acaba de comprar
        </p>
        <p className="text-sm text-brown-secondary truncate mt-0.5" data-id="element-169">
          {toast?.product.title}{' '}
          <span className="font-bold text-rose-gold" data-id="element-170">
            {toast?.product.priceText}
          </span>
        </p>
      </div>
    </div>;
}